import React, { FC, useState, useEffect, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Option, Combobox, Button, ButtonColor, ButtonSize } from '@code/innovance-react-ui-kit';
import { useForm, FieldError } from 'react-hook-form';

import {
  Title,
  FormRow,
  FormElement,
  Separator,
  ButtonsRow,
} from 'src/front/components/FormStyles';
import Datepicker from 'src/front/components/Datepicker';
import { NewSettlementSchema } from 'src/front/common/utils/validationSchemas';
import { Settlement, DocumentStorage } from 'src/front/api/apiInstance';
import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import displayPDF from 'src/front/common/utils/domUtils';

import { NewSettlementComboType } from 'src/front/api/generated';
import { optionFormat } from 'src/front/common/utils/formUtils';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { useUserInfoState } from 'src/front/contexts/SessionContext';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type

export interface NewSettlementPageData {
  beginDate: Date | null;
  endDate: Date | null;
  loanType: Option | null;
  settlementType: Option | null;
  organization: Option | null;
  insuranceCompany: Option | null;
  isComboVisible: boolean;
}
const defaultPageValues = {
  beginDate: null,
  endDate: null,
  loanType: null,
  settlementType: null,
  organization: null,
  insuranceCompany: null,
  isComboVisible: false,
};

const NewSettlement: FC = () => {
  const { t } = useTranslation();
  const resolver = useYupResolver(NewSettlementSchema, { todayMaxControl: true });
  const { processDate } = useUserInfoState();
  const [loanTypes, setLoanTypes] = useState<Option[]>([]);
  const [settlementTypes, setSettlementTypes] = useState<Option[]>([]);
  const [organizations, setOrganizations] = useState<Option[]>([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState<Option[]>([]);
  const [isSettlementTypeDisabled, setIsSettlementTypeDisabled] = useState<boolean>(false);
  const [isInsuranceComboVisible, setIsInsuranceComboVisible] = useState<boolean>(false);

  useEffect(() => {
    const getComboValues = async (): Promise<void> => {
      const { data } = await Settlement.getNewSettlementCombo({
        newSettlementComboType: NewSettlementComboType.LOANTYPE,
      });
      setLoanTypes(
        data.parameterDataList.map((value) => {
          return optionFormat(value);
        })
      );
    };
    getComboValues();
  }, []);
  const { errors, getValues, handleSubmit, register, setValue, watch } = useForm<
    NewSettlementPageData
  >({
    defaultValues: {
      ...defaultPageValues,
      beginDate: processDate || null,
      endDate: processDate || null,
    },
    resolver,
  });

  const registerFields = useCallback(() => {
    register({ name: 'loanType' });
    register({ name: 'beginDate' });
    register({ name: 'endDate' });
    register({ name: 'insuranceCompany' });
    register({ name: 'settlementType' });
    register({ name: 'organization' });
    register({ name: 'isComboVisible' });
  }, [register]);
  useEffect(() => {
    registerFields();
  }, [registerFields]);
  const handleDateChange = (fieldName: 'beginDate' | 'endDate') => (date: Date): void =>
    setValue(fieldName, date || null, {
      shouldValidate: true,
      shouldDirty: true,
    });
  const clearCombos = (isComboVisible = false): void => {
    setInsuranceCompanies([]);
    setValue('isComboVisible', isComboVisible || false);
    setIsInsuranceComboVisible(isComboVisible || false);
    setValue('insuranceCompany', null);
    setValue('organization', null);
  };
  const handleComboChange = (
    fieldName: 'loanType' | 'insuranceCompany' | 'settlementType' | 'organization'
  ) => async (value: Option | null): Promise<void> => {
    switch (fieldName) {
      case 'loanType':
        if (value) {
          const { data } = await Settlement.getNewSettlementCombo({
            newSettlementComboType: NewSettlementComboType.SETTLEMENTTYPE,
            loanType: value?.value,
          });
          setIsSettlementTypeDisabled(data.isSettlementTypeComboDisable || false);
          setSettlementTypes(
            data.parameterDataList.map((parameter) => {
              return optionFormat(parameter);
            })
          );

          setValue(
            'settlementType',
            data.isSettlementTypeComboDisable ? optionFormat(data.parameterDataList[0]) : null
          );
        } else {
          setIsSettlementTypeDisabled(false);
          setSettlementTypes([]);
          setValue('settlementType', null);
        }
        clearCombos();
        break;
      case 'settlementType':
        if (value) {
          const { data } = await Settlement.getNewSettlementCombo({
            newSettlementComboType: NewSettlementComboType.ORGANIZATION,
            settlementType: value?.value,
          });

          setOrganizations(
            data.parameterDataList.map((parameter) => {
              return optionFormat(parameter);
            })
          );
          clearCombos(data.isInsuranceComboVisible);
        } else {
          setOrganizations([]);
          clearCombos();
        }

        break;
      case 'organization':
        if (value) {
          const { data: insuranceData } = await Settlement.getNewSettlementCombo({
            newSettlementComboType: NewSettlementComboType.INSURANCECOMPANY,
            organization: value?.value,
          });

          setInsuranceCompanies(
            insuranceData.parameterDataList.map((parameter) => {
              return optionFormat(parameter);
            })
          );
          if (!insuranceData.parameterDataList.length) {
            setValue('insuranceCompany', null);
          }
        } else {
          setInsuranceCompanies([]);
          setValue('insuranceCompany', null);
        }

        break;
      default:
        break;
    }

    setValue(fieldName, value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };
  const onSubmit = async (): Promise<void> => {
    const {
      beginDate,
      endDate,
      settlementType,
      organization,
      loanType,
      insuranceCompany,
    } = getValues();
    const { data } = await Settlement.generateNewSettlementReport({
      beginDate: formatDate(beginDate),
      endDate: formatDate(endDate),
      loanType: loanType?.value || '',
      organization: organization?.value || '',
      settlementType: settlementType?.value || '',
      ...(insuranceCompany ? { insuranceCompany: insuranceCompany.value } : {}),
    });
    if (data.result === 'OK') {
      const { data: response } = await DocumentStorage.getDocument({
        documentId: data.documentId,
      });
      if (response.result === 'OK') {
        displayPDF(response.documentData);
      }
    }
  };

  return (
    <>
      <Title>
        <Trans i18nKey="newSettlement_title" />
      </Title>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <FormElement>
            <Datepicker
              errorMessage={t(errors?.beginDate?.message as string)}
              inputLabel={`${t('general_components_startDate_label')} *`}
              onChange={handleDateChange('beginDate')}
              value={watch('beginDate') as Date}
            />
          </FormElement>
          <FormElement>
            <Datepicker
              errorMessage={t(errors?.endDate?.message as string)}
              inputLabel={`${t('general_components_endDate_label')} *`}
              onChange={handleDateChange('endDate')}
              value={watch('endDate') as Date}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Combobox
              errorMessage={t((errors?.loanType as FieldError)?.message as string)}
              items={loanTypes}
              label={t('newSettlement_loanType_label')}
              name="loanType"
              onChange={handleComboChange('loanType')}
              value={watch('loanType')}
              required
            />
          </FormElement>
          {isInsuranceComboVisible ? (
            <FormElement>
              <Combobox
                errorMessage={t((errors?.insuranceCompany as FieldError)?.message as string)}
                items={insuranceCompanies}
                label={t('newSettlement_insurance_label')}
                name="insuranceCompany"
                onChange={handleComboChange('insuranceCompany')}
                value={watch('insuranceCompany')}
              />
            </FormElement>
          ) : null}
        </FormRow>
        <FormRow>
          <FormElement>
            <Combobox
              disabled={isSettlementTypeDisabled}
              errorMessage={t((errors?.settlementType as FieldError)?.message as string)}
              items={settlementTypes}
              label={t('newSettlement_settlementType_label')}
              name="settlementType"
              onChange={handleComboChange('settlementType')}
              value={watch('settlementType')}
              required
            />
          </FormElement>
          <FormElement>
            <Combobox
              errorMessage={t((errors?.organization as FieldError)?.message as string)}
              items={organizations}
              label={t('newSettlement_organization_label')}
              name="organization"
              onChange={handleComboChange('organization')}
              value={watch('organization')}
              required
            />
          </FormElement>
        </FormRow>
        <Separator />
        <ButtonsRow>
          <Button
            color={ButtonColor.Primary}
            size={ButtonSize.Medium}
            text={t('newSettlement_showReport_button')}
            type="submit"
          />
        </ButtonsRow>
      </form>
    </>
  );
};

export default NewSettlement;
